/**
 * Murphy Door Configurator - Dimension Calculator (PHASE 2: Event-Driven CCP)
 *
 * PURPOSE
 * The ONLY place in the theme that turns configurator selections into real-world
 * measurements. Everything the blueprint view draws comes from here; the UI
 * module draws numbers, it never derives them.
 *
 * WHY IT IS ITS OWN MODULE
 * Pass-through clearance was allowed to live in four places at once -- two
 * widgets, the space calculator and a CMS page -- and drifted by a quarter inch
 * in the ones nobody was testing (see CLAUDE.md "Pass-Through Math"). Dimension
 * math gets one home, with tests, so a formula revision is a single diff.
 *
 * MODULE TYPE: bl- (business logic). Pure functions over injected data. No DOM,
 * no direct module calls, no reads of raw Shopify data -- every input arrives
 * from the conductor.
 *
 * ---------------------------------------------------------------------------
 * FAIL CLOSED, PER CALLOUT
 * ---------------------------------------------------------------------------
 * A missing or provisional input omits THAT ONE dimension and leaves the rest
 * standing. It never renders NaN", never renders undefined", and never suppresses
 * the whole blueprint. On the day this ships every trim profile is provisional,
 * so customers see the four title-derived dimensions and nothing else -- which is
 * correct: a wrong measurement on a door someone frames a wall for is a refund,
 * an absent one is a phone call.
 *
 * `includeProvisional` exists ONLY for internal preview (?mdBlueprintProvisional=1)
 * and must never be defaulted to true.
 */
(function (window) {
  'use strict';

  window.MurphyDoorConfigurator = window.MurphyDoorConfigurator || {};

  const isDev = () =>
    (window.MurphyDoorConfigurator.utils &&
      window.MurphyDoorConfigurator.utils.isDevelopment &&
      window.MurphyDoorConfigurator.utils.isDevelopment()) ||
    false;

  const geometry = () => window.MurphyDoorConfigurator.blueprintGeometry || null;

  // ==========================================================================
  // PARSING -- variant titles are the authoritative source for door sizes
  // ==========================================================================

  /**
   * "30 in. (Rough Opening 32 in.)" -> { doorIn: 30, roIn: 32 }
   *
   * Deliberately tolerant of the separator and of "Rough Opening" casing, but
   * NOT of a missing R.O.: a title we cannot fully parse returns null rather
   * than guessing roIn = doorIn + 2. The +2 relationship holds across all 25
   * widths and 61 heights today, but inferring it would silently invent a
   * number the moment a non-conforming size is added to the catalog.
   */
  function parseDimensionTitle(title) {
    if (typeof title !== 'string') return null;
    const m = title.match(/(\d+(?:\.\d+)?)\s*in\..*?rough\s+opening\s*(\d+(?:\.\d+)?)\s*in\./i);
    if (!m) return null;
    const doorIn = Number(m[1]);
    const roIn = Number(m[2]);
    if (!Number.isFinite(doorIn) || !Number.isFinite(roIn)) return null;
    return { doorIn, roIn };
  }

  /**
   * "5-1/4 in. (Shelf Depth = 3-1/2 in.)" -> 5.25
   * "4-9/16 in."                          -> 4.5625
   * "Custom (Our Concierge Will Call You)" -> null  <-- REAL catalog value
   *
   * That Custom option is a live Jamb Depth variant. Anything that consumes a
   * depth must handle null; a parseFloat here would hand NaN downstream and
   * print "NaN inches" on a customer's screen.
   *
   * THIN DELEGATE — the real implementation now lives in Phase 0
   * (md-config-core-utils.js's parseFractionalInches), promoted so a Phase 2
   * UI module can call it without a peer-to-peer BL-to-BL call, which CCP
   * forbids. Kept here under the same name/signature so every existing
   * caller, and the brace-matching test-extraction technique in
   * tests/assets/dimension-calculator.test.js and
   * tests/assets/blueprint-overlay-toggle.test.js, keep working unmodified.
   */
  function parseFractionalInches(title) {
    return window.MurphyDoorConfigurator.utils.parseFractionalInches(title);
  }

  /**
   * "5-1/4 in. (Shelf Depth = 3-1/2 in.)" -> 3.5
   *
   * The shelf depth is a real, useful metric the customer currently never sees
   * anywhere in the configurator -- it rides along inside the Door Depth title
   * and nothing reads it. Free to surface once leaders exist.
   */
  function parseShelfDepth(title) {
    if (typeof title !== 'string') return null;
    // Same two spellings as parseFractionalInches, and the live Door Depth title
    // puts the number BEFORE the word: '8-1/4" Door | 6-1/2" Shelf'. The older
    // '(Shelf Depth = 6-1/2 in.)' form puts it after. Both are matched.
    const m =
      title.match(/shelf\s+depth\s*=?\s*(\d+)(?:-(\d+)\/(\d+))?\s*(?:in\.|["\u2033])/i)
      || title.match(/(\d+)(?:-(\d+)\/(\d+))?\s*(?:in\.|["\u2033])\s*shelf/i);
    if (!m) return null;
    const whole = Number(m[1]);
    if (!Number.isFinite(whole)) return null;
    if (m[2] && m[3]) {
      const den = Number(m[3]);
      return den ? whole + Number(m[2]) / den : whole;
    }
    return whole;
  }

  /** "Left Hand Inswing" -> { hand: 'Left', swing: 'Inswing' } */
  function parseHandingTitle(title) {
    if (typeof title !== 'string') return null;
    const m = title.match(/(Left|Right)\s+Hand\s+(In|Out)swing/i);
    if (!m) return null;
    return {
      hand: m[1][0].toUpperCase() + m[1].slice(1).toLowerCase(),
      swing: m[2].toLowerCase() === 'in' ? 'Inswing' : 'Outswing',
    };
  }

  /**
   * Product title + category -> trim profile key.
   * "Fluted Front Casing (Paint Grade) (Single Door)" + "Front Casing"
   *   -> "front-casing-fluted"
   *
   * Keyed on the SHAPE word, so all 7 Front Casing products collapse onto the 2
   * profiles that actually differ. Adding a new colorway needs no code change;
   * adding a new SHAPE needs a new trimProfiles entry, and until it exists the
   * lookup returns undefined and the callout omits itself.
   */
  function trimProfileKey(category, productTitle) {
    if (!category || typeof productTitle !== 'string') return null;
    const shape = /^(Standard|Fluted|Square|Crown|Flat|Mirror)\b/i.exec(productTitle.trim());
    if (!shape) return null;
    const slug = String(category).toLowerCase().replace(/[^a-z]+/g, '-').replace(/^-|-$/g, '');
    return `${slug}-${shape[1].toLowerCase()}`;
  }

  /**
   * Engineering's "-1 RULE" (sheet B9, R. Cooley, rev. 2026-09-25), scope FMD2
   * SINGLE door:
   *
   *   (In-Swing AND jamb > 4-9/16in) OR (Out-Swing AND depth >= 10in)
   *
   * ONE predicate, two consequences, both confirmed by engineering:
   *   - the manufactured SLAB narrows 1in (the 'door-size' callout), and
   *   - the casing widens 3-3/4in -> 4-3/4in (overall width, hinge-side
   *     reveal, entablature width and both overall heights) -- EXCEPT Fluted
   *     casing, which is milled by an outside vendor and always stays 3-3/4in.
   *     That exemption lives on the trim profile (`wideRuleExempt`), not here,
   *     because it changes the casing consequence only; the slab -1 still
   *     applies to a fluted door.
   *
   * Until 2026-09-25 the slab arm gated out-swing on depth > 8-1/4in and the
   * casing arm on depth >= 10in, each from its own source. Identical on the
   * live depths {5-1/4, 6-1/2, 8-1/4, 10, 12}; they would have split on a 9in
   * depth. Unified on the sheet's >= 10.
   *
   * Strict `>` on jamb (4-9/16 itself is NOT wide), `>=` on depth (10 IS).
   *
   * THIN DELEGATE (2026-09-25) -- the predicate now lives in Phase 0
   * (md-config-core-utils.js meetsMinusOneRule) so the space calculator's
   * in-swing pass-through (sheet B24) reads the same rule without a BL-to-BL
   * call. Same name/signature here so every existing caller is unchanged.
   */
  function meetsMinusOneRule(ctx) {
    return window.MurphyDoorConfigurator.utils.meetsMinusOneRule(ctx);
  }

  // ==========================================================================
  // FORMATTING
  // ==========================================================================

  /**
   * 40.5 -> '40 1/2"',  38 -> '38"',  4.5625 -> '4 9/16"'.
   *
   * THIN DELEGATE — the real implementation now lives in Phase 0
   * (md-config-core-utils.js's formatInches), promoted so a Phase 2 UI module
   * can call it without a peer-to-peer BL-to-BL call, which CCP forbids. Kept
   * here under the same name/signature so every existing caller, and the
   * brace-matching test-extraction technique in
   * tests/assets/dimension-calculator.test.js, keep working unmodified.
   */
  function formatInches(value, denominator) {
    return window.MurphyDoorConfigurator.utils.formatInches(value, denominator);
  }

  // ==========================================================================
  // SELECTION READING
  // ==========================================================================

  /**
   * Pulls the dimension-relevant facts out of the applied-selections Map.
   * @param {Map<string, {displayTitle: string, productTitle: string}>} applied
   */
  function readSelectionContext(applied) {
    const ctx = {
      width: null, height: null, depthIn: null, jambIn: null,
      hand: null, swing: null, shelfDepthIn: null,
      frontCasingKey: null, backCasingKey: null,
      frontEntablatureKey: null, backEntablatureKey: null,
    };
    if (!applied || typeof applied.get !== 'function') return ctx;

    const get = (cat) => {
      const sel = applied.get(cat);
      return sel && typeof sel === 'object' ? sel : null;
    };

    const w = get('Width');
    if (w) ctx.width = parseDimensionTitle(w.displayTitle);
    const h = get('Height');
    if (h) ctx.height = parseDimensionTitle(h.displayTitle);

    const d = get('Door Depth');
    if (d) {
      ctx.depthIn = parseFractionalInches(d.displayTitle);
      ctx.shelfDepthIn = parseShelfDepth(d.displayTitle);
    }
    const j = get('Jamb Depth');
    if (j) ctx.jambIn = parseFractionalInches(j.displayTitle);

    const hand = get('Door Handing');
    if (hand) {
      const parsed = parseHandingTitle(hand.displayTitle);
      if (parsed) { ctx.hand = parsed.hand; ctx.swing = parsed.swing; }
    }

    const fc = get('Front Casing');
    if (fc) ctx.frontCasingKey = trimProfileKey('Front Casing', fc.productTitle);
    const bc = get('Back Casing');
    if (bc) ctx.backCasingKey = trimProfileKey('Back Casing', bc.productTitle);
    const fe = get('Front Entablature');
    if (fe) ctx.frontEntablatureKey = trimProfileKey('Front Entablature', fe.productTitle);
    const be = get('Back Entablature');
    if (be) ctx.backEntablatureKey = trimProfileKey('Back Entablature', be.productTitle);

    return ctx;
  }

  // ==========================================================================
  // THE FORMULAS
  // ==========================================================================

  /**
   * Builds the callout set for one view.
   *
   * @param {Map} applied            conductor.getAppliedSelectionsMap()
   * @param {Object} [options]
   * @param {'front'|'back'} [options.view='front']
   * @param {boolean} [options.includeProvisional=false]  preview only
   * @returns {{callouts: Array, context: Object, suppressed: Array}}
   */
  /**
   * Pair two inch values into one label: "38" x 82"".
   * Rough opening and door unit are each a W x H, and pairing them onto one
   * label is what lets each occupy a single margin block instead of two.
   */
  function formatPair(wIn, hIn) {
    const w = formatInches(wIn);
    const h = formatInches(hIn);
    return w && h ? `${w} × ${h}` : null;
  }

  /**
   * The measurements a customer sees, as data. No canvas coordinates: the UI
   * maps each id onto blueprintGeometry.CALLOUT_SLOTS, so a re-layout of the
   * sheet never touches this module.
   *
   * `options.spaceData` is the `diagram` block published by
   * md-config-bl-space-calculator.js. Pass-through AND wall clearance are both
   * READ from it, never re-derived here -- that module owns the jamb- and
   * casing-aware in-swing pass-through (computeInSwingPassThrough, sheet B24),
   * the depth-keyed OUT_SWING_SPECS table and the
   * jamb-aware in-swing wall clearance (computeInSwingWallClearance, sheet B23),
   * and its own comment records why: these
   * numbers "got re-derived in multiple places and drifted".
   */
  function computeCallouts(applied, options) {
    const opts = options || {};
    const view = opts.view === 'back' ? 'back' : 'front';
    const includeProvisional = opts.includeProvisional === true;
    const space = opts.spaceData || null;
    const geo = geometry();
    const profiles = (geo && geo.trimProfiles) || {};

    const ctx = readSelectionContext(applied);
    const callouts = [];
    const suppressed = [];

    /**
     * Single gate every callout passes through.
     * `provisional` no longer suppresses on its own: since 2026-09-04 the overlay
     * passes includeProvisional=true by default (product owner's call), so these
     * callouts render and the flag serves as the record of what engineering has
     * yet to confirm. The gate itself is kept intact so it can be re-armed.
     *
     * `provisional` here is not advisory -- it is the reason the dimension does
     * not exist for a customer.
     */
    const add = (spec) => {
      const label = spec.label != null
        ? spec.label
        : (Number.isFinite(spec.valueIn) ? formatInches(spec.valueIn) : null);
      if (!label) {
        suppressed.push({ id: spec.id, reason: Number.isFinite(spec.valueIn) ? 'unformattable' : 'missing-input' });
        return;
      }
      if (spec.provisional && !includeProvisional) {
        suppressed.push({ id: spec.id, reason: 'provisional' });
        return;
      }
      /* `extra` is a SECOND measurement that shares this callout's block and
         leader, for a dimension that has no anchor of its own to point at (see
         shelf depth below). It fails closed INDEPENDENTLY: an extra we cannot
         format is dropped and the primary dimension still prints, because the
         two are separate facts and losing one is no reason to lose both. */
      let extra = null;
      if (spec.extra) {
        const extraLabel = spec.extra.label != null
          ? spec.extra.label
          : (Number.isFinite(spec.extra.valueIn) ? formatInches(spec.extra.valueIn) : null);
        if (extraLabel) extra = { ...spec.extra, label: extraLabel };
        else suppressed.push({ id: `${spec.id}:extra`, reason: 'missing-input' });
      }
      callouts.push({ ...spec, label, extra });
    };

    // --- Authoritative: straight from the variant titles -------------------

    /* Engineering (Cooley, 2026-09-11; threshold unified 2026-09-25): the
       manufactured door narrows 1in under the "-1 RULE" (meetsMinusOneRule):
         - OUT-SWING doors 10in and deeper lose 1in at the BACK non-hinge corner.
         - IN-SWING doors with jamb depth over 4-9/16in lose 1in at the FRONT
           non-hinge corner.
       Applies to Fluted casing too -- only the casing widening is exempt.
       This is the manufactured DOOR narrowing inside the SAME rough opening
       the customer frames -- not the R.O. shrinking -- so it touches only the
       'door-size' callout's width, never ctx.width.roIn or ctx.width itself.
       Deliberately NOT applied to `plan.doorWidthIn` (the birdseye leaf) or
       anywhere the space calculator's pass-through/wall-clearance numbers are
       matched against ctx.width.doorIn (see spaceMatchesSelection below) --
       silently shifting the number they compare against would flag live data
       as stale for exactly these configurations. */
    const swingClearanceReductionIn = meetsMinusOneRule(ctx) ? 1 : 0;

    if (ctx.width && ctx.height) {
      add({
        id: 'ro-size', title: 'Rough opening', priority: 1, provisional: false,
        label: formatPair(ctx.width.roIn, ctx.height.roIn),
        widthIn: ctx.width.roIn, heightIn: ctx.height.roIn,
      });
      const doorUnitWidthIn = ctx.width.doorIn - swingClearanceReductionIn;
      add({
        id: 'door-size', title: 'Door unit', priority: 1, provisional: false,
        label: formatPair(doorUnitWidthIn, ctx.height.doorIn),
        widthIn: doorUnitWidthIn, heightIn: ctx.height.doorIn,
      });
    } else {
      suppressed.push({ id: 'ro-size', reason: 'missing-input' });
      suppressed.push({ id: 'door-size', reason: 'missing-input' });
    }

    // Depth is the axis a flat elevation cannot draw at all. It lives in the
    // plan detail, where the leaf's THICKNESS is the dimension.
    //
    // Shelf depth rides ALONG WITH it rather than as a callout of its own,
    // because it has no anchor: it is the usable depth INSIDE the case, not an
    // edge of the drawn leaf, so a leader for it would point at nothing. It is
    // the number a customer actually shops with -- what fits on the shelf --
    // and until now it existed only inside the variant title and was read by
    // nobody.
    //
    // 🚨 READ FROM THE TITLE, NEVER `depth - 1-3/4`. That offset holds across
    // all five live depths today, but it is a MANUFACTURING fact owned by the
    // catalog, not a formula this module gets to assert. Re-deriving a
    // catalog-owned number in a second place is exactly what put the
    // "Pass-Through Math" entry in CLAUDE.md. A depth whose title carries no
    // shelf value prints the door depth alone.
    if (Number.isFinite(ctx.depthIn)) {
      add({
        id: 'door-depth', title: 'Door depth', priority: 2, provisional: false,
        valueIn: ctx.depthIn,
        // Handed over UNCONDITIONALLY, with a null value when the title carries
        // no shelf. The `add` gate then records WHY it is absent, the same way
        // it does for every other dimension, instead of the extra quietly never
        // having existed -- an absent shelf depth on a live depth means the
        // catalog title changed shape, and that is worth being able to see.
        extra: { title: 'Shelf depth', valueIn: ctx.shelfDepthIn },
      });
    } else {
      suppressed.push({ id: 'door-depth', reason: 'missing-input' });
    }

    if (Number.isFinite(ctx.jambIn)) {
      add({ id: 'jamb-depth', title: 'Jamb depth', priority: 2, provisional: false, valueIn: ctx.jambIn });
    } else if (applied && typeof applied.get === 'function' && applied.get('Jamb Depth')) {
      // The live "Custom (Our Concierge Will Call You)" option parses to null.
      // Say so rather than printing nothing, so the customer sees a choice they
      // made and not a gap in the drawing.
      add({ id: 'jamb-depth', title: 'Jamb depth', priority: 2, provisional: false, label: 'Custom' });
    } else {
      suppressed.push({ id: 'jamb-depth', reason: 'missing-input' });
    }

    // --- Pass-through: READ from the space calculator, never re-derived -----
    //
    // 🚨 THE CACHED PAYLOAD IS VALIDATED AGAINST THE CURRENT SELECTION, because
    // the space calculator only publishes on its SUCCESS path. When it cannot
    // compute -- missing selections, or an error -- it calls
    // uiUpdater.batchUpdate({passThroughSpace: {isVisible: false}}) and returns
    // without emitting BUSINESS_SPACE_CALCULATION_COMPLETED at all. A consumer
    // caching the last good payload therefore keeps drawing a number the system
    // has already stopped standing behind.
    //
    // The diagram carries the doorWidth and doorDepth it was computed FROM, so
    // the cache can check itself. Matching on those rather than clearing on
    // every selection change also avoids the span flickering out and back on
    // changes that cannot affect it.
    const spaceMatchesSelection = !!space
      && (!Number.isFinite(space.doorWidth) || !ctx.width
          || Math.abs(space.doorWidth - ctx.width.doorIn) < 0.01)
      && (!Number.isFinite(space.doorDepth) || !Number.isFinite(ctx.depthIn)
          || Math.abs(space.doorDepth - ctx.depthIn) < 0.01);

    /* In-swing pass-through depends on the JAMB and the front CASING too
       (sheet B24, FINAL 2026-09-25: W - (jamb + 0.5) - (casingWidth - 1.5)),
       so it passes the same jamb gate as wall clearance below, plus a casing
       gate on the Fluted exemption the payload was computed with. Both are
       absent on out-swing payloads, which therefore always match. A missing
       front casing reads as Standard (not exempt) on both sides. */
    const spaceJambMatches = !space
      || !Number.isFinite(space.jambDepth)
      || !Number.isFinite(ctx.jambIn)
      || Math.abs(space.jambDepth - ctx.jambIn) < 0.01;
    const frontCasingProfile = ctx.frontCasingKey ? profiles[ctx.frontCasingKey] : null;
    const spaceCasingMatches = !space
      || typeof space.casingWideRuleExempt !== 'boolean'
      || space.casingWideRuleExempt
        === !!(frontCasingProfile && frontCasingProfile.wideRuleExempt === true);

    if (space && (!spaceMatchesSelection || !spaceJambMatches || !spaceCasingMatches)) {
      suppressed.push({ id: 'pass-through', reason: 'stale-space-data' });
    } else if (spaceMatchesSelection && Number.isFinite(space.passThrough)) {
      /* titleLines pre-breaks the phrase the same way WALL_CLEARANCE_TITLE
         does below -- "Pass-through" and "space" are one idea, so where it
         wraps is a copy decision made here, not a width-based auto-wrap that
         would split it differently at every container size. `title` stays the
         joined form for frameSummary (the screen-reader path) and for
         drawIsoDetail's diagram feed, which reads .label, not .title. */
      const PASS_THROUGH_TITLE = ['Pass-through', 'space'];
      add({
        id: 'pass-through',
        title: PASS_THROUGH_TITLE.join(' '),
        titleLines: PASS_THROUGH_TITLE,
        sub: '(door wide open)',
        priority: 2, provisional: false,
        // Take the NUMBER from the space calculator, format it here.
        //
        // Its own passThroughDisplay resolves to EIGHTHS only (its fraction
        // table stops at 1/8, 3/8, 5/8, 7/8) -- the exact rounding that
        // silently corrupted the 4-9/16in jamb to 4-5/8in and put a sixteenths
        // rule in CLAUDE.md. formatInches does sixteenths, and also emits the
        // prime glyph the rest of the sheet uses rather than a straight quote.
        // Re-formatting a number is presentation; it is not re-deriving it, and
        // the value still has exactly one owner.
        label: formatInches(space.passThrough),
        valueIn: space.passThrough,
      });
    } else if (!space) {
      suppressed.push({ id: 'pass-through', reason: 'no-space-data' });
    } else {
      // No in-swing pass-through exists at 5-1/4in depth -- SUPPORTED_DEPTHS
      // .IN_SWING excludes it -- so the calculator legitimately returns nothing
      // and the span is omitted rather than guessed at.
      suppressed.push({ id: 'pass-through', reason: 'not-applicable' });
    }

    // --- Wall clearance: same payload, same staleness gate -----------------
    //
    // IN-SWING ONLY, and the space calculator says so structurally rather than
    // by a flag we would have to interpret: `wallClearance` is undefined on an
    // out-swing result BY DESIGN, because an out-swing door swings away from
    // the wall and there is no clearance to state. A falsy value is therefore
    // an ANSWER, not a gap -- so it is suppressed rather than printed as
    // "None". The blueprint states dimensions that exist; the widget's
    // reassuring "no clearance needed" copy belongs to a surface that has a
    // sentence to spend on it, and the out-swing CAD has no annotation group to
    // hang it on either (see pass-through-diagrams.js applyMeasurements).
    //
    // 🚨 READ, NEVER RE-DERIVE -- the same rule pass-through follows directly
    // above, for the same reason. md-config-bl-space-calculator.js owns
    // computeInSwingWallClearance (engineering sheet B23, depth AND jamb --
    // the selected jamb is passed in there), and the "Pass-Through Math" footgun exists
    // because the single-door 3/4in reveal series got re-implemented elsewhere
    // as the French 1/2in one and drifted by 1/4in at every depth. Formatting
    // it here is presentation; the value still has exactly one owner.
    /* In-swing wall clearance depends on the JAMB too (sheet B26, rev.
       2026-09-25), so it reuses spaceJambMatches from the pass-through gate
       above (not the casing gate -- wall clearance has no casing term). An
       unparseable selected jamb (Custom) has no number to compare, and the
       calculator used the standard 4-9/16in. */
    if (space && (!spaceMatchesSelection || !spaceJambMatches)) {
      suppressed.push({ id: 'wall-clearance', reason: 'stale-space-data' });
    } else if (
      spaceMatchesSelection
      && Number.isFinite(space.wallClearance)
      && space.wallClearance > 0
    ) {
      /* No `sub`. Pass-through carries "(door wide open)" because its number is
         meaningless without the condition it was measured under; the clearance
         is unconditional. The blueprint block prints the title ONLY -- see
         `valueInArt` on its slot -- because the drawing already states the
         figure at the point of measurement. The label is still carried: the
         drawing writes it from here, and it is what a screen reader hears from
         frameSummary.
         
         🚨 `titleLines` IS THE SOURCE, `title` IS DERIVED FROM IT -- never the
         other way round and never both hand-written, or the spoken title and
         the drawn one drift apart with nothing to catch it. Where the phrase
         breaks is a copy decision ("from jamb" is one idea and stays on one
         line), so it is made here with the words rather than by a width-based
         auto-wrap in the renderer that would split it differently at every
         container size. */
      const WALL_CLEARANCE_TITLE = ['Wall', 'clearance', 'needed', 'from jamb'];
      add({
        id: 'wall-clearance',
        title: WALL_CLEARANCE_TITLE.join(' '),
        titleLines: WALL_CLEARANCE_TITLE,
        priority: 2, provisional: false,
        label: formatInches(space.wallClearance),
        valueIn: space.wallClearance,
      });
    } else if (!space) {
      suppressed.push({ id: 'wall-clearance', reason: 'no-space-data' });
    } else {
      suppressed.push({ id: 'wall-clearance', reason: 'not-applicable' });
    }

    // --- Swing clearance: same payload, same staleness gates ---------------
    //
    // Sheet B24 (rev. 2026-09-25 evening), engineering's definition: "the
    // distance from the wall to the furthest point on the door when open".
    //   In-Swing:  Width_CASE == 3.75 ? W - 1.25 : W - 2.25
    //   Out-Swing: W - 4
    //
    // 🚨 NOT WALL CLEARANCE. Two different quantities that share a word:
    // wall clearance (B25, above) is how much room the open door needs BESIDE
    // the jamb on the back wall; swing clearance is how far the open door
    // reaches OUT from the wall. They are separate callouts on purpose -- never
    // merge them or feed one from the other.
    //
    // 🚨 READ, NEVER RE-DERIVE -- md-config-bl-space-calculator.js owns it
    // (computeSwingClearance), exactly like pass-through and wall clearance.
    // In-swing depends on the jamb and the front casing's Fluted exemption
    // (through Width_CASE), so it passes the same jamb + casing gates as
    // pass-through; out-swing payloads carry neither and always match. It
    // ALSO depends on the swing itself, so the payload's doorType must match
    // the selected handing, and an unknown swing states nothing -- the
    // birdseye's example art must not carry a number for a door the customer
    // has not chosen.
    const swingKnown = ctx.swing === 'Inswing' || ctx.swing === 'Outswing';
    const spaceSwingMatches = !space
      || typeof space.doorType !== 'string'
      || !swingKnown
      || (space.doorType === 'In-Swing') === (ctx.swing === 'Inswing');
    if (space && (!spaceMatchesSelection || !spaceJambMatches || !spaceCasingMatches
        || !spaceSwingMatches)) {
      suppressed.push({ id: 'swing-clearance', reason: 'stale-space-data' });
    } else if (!space) {
      suppressed.push({ id: 'swing-clearance', reason: 'no-space-data' });
    } else if (!swingKnown) {
      suppressed.push({ id: 'swing-clearance', reason: 'swing-unknown' });
    } else if (Number.isFinite(space.swingClearance)) {
      /* titleLines is the source, title is derived -- same rule as
         WALL_CLEARANCE_TITLE. One line: "Swing clearance" is one idea and fits
         the 16-character gutter budget. The value is printed in the block (no
         valueInArt): the CAD carries no annotation for this dimension. */
      const SWING_CLEARANCE_TITLE = ['Swing clearance'];
      add({
        id: 'swing-clearance',
        title: SWING_CLEARANCE_TITLE.join(' '),
        titleLines: SWING_CLEARANCE_TITLE,
        sub: '(door open)',
        priority: 2, provisional: false,
        label: formatInches(space.swingClearance),
        valueIn: space.swingClearance,
      });
    } else {
      suppressed.push({ id: 'swing-clearance', reason: 'not-applicable' });
    }

    // --- Trim-dependent: provisional until engineering confirms -------------

    const casingKey = view === 'back' ? ctx.backCasingKey : ctx.frontCasingKey;
    const casing = casingKey ? profiles[casingKey] : null;
    const entKey = view === 'back' ? ctx.backEntablatureKey : ctx.frontEntablatureKey;
    const ent = entKey ? profiles[entKey] : null;

    /* The engineering sheet's "-1 RULE" (B9): the casing widens 3-3/4in ->
       4-3/4in, which also pushes overall width, the hinge-side reveal,
       entablature width and both overall heights. Sheet scope is the FMD2
       SINGLE door. Same predicate as the slab -1 above (meetsMinusOneRule) --
       engineering confirmed 2026-09-25 that they are one rule.

       🚨 FLUTED IS EXEMPT (Cooley, rev. 2026-09-25): fluted casing is milled by
       an outside vendor and is always 3-3/4in, so every casing-driven
       dimension keeps the 3-3/4in geometry. The flag lives on the trim profile
       of the casing in THIS view, so a fluted front with a standard back (or
       vice versa) resolves each face correctly. With no casing selected the
       entablature follows the rule, as for Standard. */
    const ruleMet = meetsMinusOneRule(ctx);
    const casingIsWide = ruleMet
      && !(casing && casing.wideRuleExempt === true);

    /* Trim resolvers. A sheet-confirmed profile carries structured values; a
       profile engineering has not confirmed keeps its legacy scalar. Both
       shapes stay readable so an unconfirmed profile still renders.

       Non-hinge side (sheet rev. 2026-09-25 evening):
         - casing widens (rule met, not exempt) -> nonHingeWide, else nonHinge
           (back C18 = Width_CASE - 1 = 3.75; front B18 stays 1.5)
         - rule met but THIS casing is exempt (Fluted) -> nonHingeUnderRule,
           else nonHinge (front Fluted B18 = 0.5: B14 takes the slab -1 without
           the casing +1). */
    const resolveCasingEdges = (c) => {
      if (!c) return null;
      const e = c.roToCasingEdgeIn;
      if (e) {
        let nonHinge = e.nonHinge;
        if (casingIsWide && Number.isFinite(e.nonHingeWide)) nonHinge = e.nonHingeWide;
        else if (!casingIsWide && ruleMet && Number.isFinite(e.nonHingeUnderRule)) {
          nonHinge = e.nonHingeUnderRule;
        }
        return { hinge: casingIsWide ? e.hingeWide : e.hinge, nonHinge };
      }
      // Legacy symmetric fallback -- one spread reported on both sides.
      return Number.isFinite(c.spreadPerSideIn)
        ? { hinge: c.spreadPerSideIn, nonHinge: c.spreadPerSideIn }
        : null;
    };
    const resolveEntSpread = (e) => {
      if (!e) return null;
      const sp = e.spreadPerSideIn;
      if (sp && typeof sp === 'object') return casingIsWide ? sp.wide : sp.normal;
      return Number.isFinite(sp) ? sp : null;
    };
    /* Returns null when the profile knows its two swings but the customer has
       not picked one yet -- the number is genuinely unknowable at that moment,
       so the callout is suppressed rather than defaulted to in-swing. */
    const resolveEntTopAboveRo = (e) => {
      if (!e) return null;
      const t = e.topAboveRoIn;
      if (t && typeof t === 'object') {
        if (ctx.swing !== 'Inswing' && ctx.swing !== 'Outswing') return null;
        return ctx.swing === 'Outswing'
          ? (casingIsWide ? t.outswingWide : t.outswing)
          : (casingIsWide ? t.inswingWide : t.inswing);
      }
      return Number.isFinite(t) ? t : null;
    };

    const edges = resolveCasingEdges(casing);
    const entSpreadPerSideIn = resolveEntSpread(ent);
    const entTopAboveRoIn = resolveEntTopAboveRo(ent);

    /* FRONT entablature width is stated off the FRONT casing-to-casing width,
       not the R.O. (sheet B15, rev. 2026-09-25 evening):
         Flat (Standard) B14 + 1.25 | Square B14 + 2.75 | Crown B14 + 7
       so it inherits every B14 branch, including Fluted under the rule. With
       no front casing selected yet, B14 is taken for Standard (the sheet's
       Flat default) -- the same "no casing reads as Standard" convention the
       casingIsWide gate above uses. */
    let entWidthIn = null;
    if (ctx.width && ent) {
      if (Number.isFinite(ent.widthOverCasingIn)) {
        const baseEdges = edges || resolveCasingEdges(profiles['front-casing-standard']);
        if (baseEdges) {
          entWidthIn = ctx.width.roIn + baseEdges.hinge + baseEdges.nonHinge + ent.widthOverCasingIn;
        }
      } else if (Number.isFinite(entSpreadPerSideIn)) {
        /* BACK: R.O. + 2*spread. All three styles now encode sheet C15
           (rev. 2026-09-28): Standard W + 2*(Width_CASE + 0.625), Square
           W + 2*(Width_CASE + 0.375), Crown W + 2*(Width_CASE + 2.5) -- see
           the ENTABLATURE note in md-config-blueprint-geometry.js. */
        entWidthIn = ctx.width.roIn + 2 * entSpreadPerSideIn;
      }
    }

    if (ctx.width && edges) {
      /* 🚨 CASING TO CASING, AND ONLY THE CASING -- the entablature is
         DELIBERATELY excluded from this number even though it is wider.
         Measured off the renders, EVERY entablature flares past the casing it
         sits on: front 1.78 / 2.58 / 4.98in per side against the casing's
         1.28in, back 3.375 / 4.125 / 6.25in (normal casing, sheet C15) against
         3.37in. So a "widest point" reading of overall width is always the
         entablature's, and on Crown it runs 7.4in wider in total than the
         trim the customer sees beside the door.

         The pair of callouts is what makes excluding it safe: width is the
         casing surround (floor to head, the part that has to clear furniture
         and adjacent trim) and HEIGHT is explicitly floor-to-entablature, so
         the head moulding is dimensioned -- just on the axis it actually
         governs. The two sub-lines together name the trim assembly's bounding
         box the way a door surround is normally dimensioned.

         If this ever needs to report the head flare on the width axis too,
         that is a SECOND callout, not a swap of this one's basis: silently
         showing the entablature's number under a "casing to casing" label is
         the wrong number wearing the right label. */
      add({
        id: 'overall-width', title: 'Overall width',
        sub: '(casing to casing)',
        priority: 3,
        valueIn: ctx.width.roIn + edges.hinge + edges.nonHinge,
        provisional: casing.provisional === true,
      });
    } else if (ctx.width) {
      suppressed.push({ id: 'overall-width', reason: casingKey ? 'no-profile' : 'no-casing-selected' });
    }

    if (ctx.height && ent && Number.isFinite(entTopAboveRoIn)) {
      add({
        id: 'overall-height', title: 'Overall height',
        // Names the span, and it is load-bearing copy rather than decoration:
        // it is what tells the customer the head moulding IS accounted for,
        // given overall width deliberately stops at the casing.
        sub: '(floor to entablature)',
        priority: 3,
        valueIn: ctx.height.roIn + entTopAboveRoIn,
        // swingDependent means the single stored value covers both swings and is
        // known incomplete. Even once provisional clears, this must stay gated
        // until there are separate in/out numbers.
        provisional: ent.provisional === true || ent.swingDependent === true,
      });
    } else if (ctx.height && entKey) {
      const swingKnown = ctx.swing === 'Inswing' || ctx.swing === 'Outswing';
      const swingAware = !!(ent && ent.topAboveRoIn && typeof ent.topAboveRoIn === 'object');
      suppressed.push({
        id: 'overall-height',
        reason: swingAware && !swingKnown ? 'swing-unknown' : 'no-profile',
      });
    }

    /* Entablature width -- the SECOND callout the comment above 'overall-width'
       calls for, on the axis the head moulding actually governs. The value is
       resolved into entWidthIn above: FRONT from the sheet (B15, off the front
       casing-to-casing width); BACK from sheet C15 for all three styles
       (Standard 5/8in past the back casing per side; Square/Crown per the
       C15 formula, confirmed rev. 2026-09-28). */
    if (Number.isFinite(entWidthIn)) {
      // titleLines: 'Entablature width' is 18 characters and the left gutter's
      // 370px-container budget is 16 (see the title-fit test) -- the same
      // titleLines/title split 'wall-clearance' and 'pass-through' already use.
      const ENTABLATURE_WIDTH_TITLE = ['Entablature', 'width'];
      add({
        id: 'entablature-width', title: ENTABLATURE_WIDTH_TITLE.join(' '),
        titleLines: ENTABLATURE_WIDTH_TITLE,
        priority: 3,
        valueIn: entWidthIn,
        // widthProvisional: no profile currently sets this (back Square/Crown
        // were the last holdouts; sheet C15 confirmed rev. 2026-09-28). Left
        // wired so a future unconfirmed profile can still flag itself.
        provisional: ent.provisional === true || ent.swingDependent === true
          || ent.widthProvisional === true,
      });
    } else if (ctx.width && entKey) {
      suppressed.push({ id: 'entablature-width', reason: 'no-profile' });
    }

    /* Rough-opening-to-casing-edge -- the per-side reveal stated as its own
       dimension rather than only living inside the overall-width formula.

       This USED to report one symmetric number on both leaders, because the
       asymmetry was known from CAD but no confirmed per-side split existed.
       The engineering sheet supplies it: front hinge 1.75in (2.75in wide) vs
       non-hinge 1.5in; back 2.75in both sides. Two unrelated sources agree on
       the direction -- the FBX models put MORE overlap on the hinge side, and
       so does the sheet.

       🚨 DO NOT KEY THESE TO ctx.hand. It is the obvious-looking move and it is
       WRONG -- it double-mirrors. The base art is authored left-hand with the
       hinge on the LEFT, and for a right-hand door the OVERLAY already mirrors
       everything together: drawSilhouettes() flips the art, and drawLabels()
       is passed mirrorSides = (plan.hand === 'Right'), which both swaps each
       slot's gutter (`slot.side !== 'left'`) and reflects its dot's x. The
       'left' slot's dot also rides `dotRidesCasingEdge: 'L'` at canvas x=584,
       i.e. DOOR_UNIT.L, the authored hinge-side edge.

       So 'ro-to-casing-edge-left' always points at the same PHYSICAL edge --
       the hinge one -- in both handings; only where it appears on screen
       changes. Assigning by hand here would swap the values a second time and
       put the 1.75in hinge reveal on the 1.5in non-hinge edge for every
       right-hand door. It also means handing is NOT required for these two:
       with no handing chosen the art is drawn unmirrored, so hinge-on-left
       still holds. */
    if (edges) {
      // titleLines, same reason as entablature-width above: the joined title
      // is 29 characters against a 370px-container budget of 15-16.
      const RO_TO_CASING_EDGE_TITLE = ['Rough opening', 'to casing edge'];
      const valueForSide = { left: edges.hinge, right: edges.nonHinge };
      ['left', 'right'].forEach((side) => {
        add({
          id: `ro-to-casing-edge-${side}`, title: RO_TO_CASING_EDGE_TITLE.join(' '),
          titleLines: RO_TO_CASING_EDGE_TITLE,
          priority: 3,
          valueIn: valueForSide[side],
          provisional: casing.provisional === true,
        });
      });
    } else if (casingKey) {
      suppressed.push({ id: 'ro-to-casing-edge-left', reason: 'no-profile' });
      suppressed.push({ id: 'ro-to-casing-edge-right', reason: 'no-profile' });
    }

    // --- Plan detail context ------------------------------------------------
    // Handedness comes from the space calculator's handlingLabel when it is
    // there (it carries the raw variant title, which is the only place left vs
    // right survives) and falls back to what we parsed ourselves.
    // 🚨 DEPTH ALONE IS ENOUGH TO DRAW THE PLAN. It used to require handing too,
    // which silently swallowed a dimension the customer had already chosen: on
    // the live page, picking Door Depth showed nothing until Door Handing was
    // also picked, because depth's leader anchors onto the plan and the plan did
    // not exist yet. hand/swing are nullable now -- the renderer draws the door
    // SHUT when they are unknown, which states both depths honestly and claims
    // nothing about a swing nobody has chosen.
    const plan = (Number.isFinite(ctx.depthIn) && ctx.width)
      ? {
          hand: ctx.hand,
          swing: ctx.swing,
          doorWidthIn: ctx.width ? ctx.width.doorIn : null,
          doorDepthIn: ctx.depthIn,
          jambIn: Number.isFinite(ctx.jambIn) ? ctx.jambIn : null,
          passThroughIn: spaceMatchesSelection && Number.isFinite(space.passThrough)
            ? space.passThrough
            : null,
        }
      : null;
    if (!plan) suppressed.push({ id: 'plan-detail', reason: 'missing-input' });

    callouts.sort((a, b) => a.priority - b.priority);
    return { callouts, plan, context: ctx, suppressed };
  }

  // ==========================================================================
  // MEASUREMENT-MODE AUTO-SWITCH
  //
  // Deliberately mirrors md-config-bl-image-layer-coordinator.js's
  // detectAutoToggleNeeded(): derive the decision from what content is
  // AVAILABLE, and return null for "no change" rather than forcing a state.
  // That function decides WHICH FACE (front/back); this one decides WHICH
  // REPRESENTATION (measurements/product). The two axes are independent, so all
  // four combinations are reachable and neither system overrides the other.
  //
  // Availability, not a hardcoded list. A literal category list was the first
  // implementation and it rots: every catalog change needs a code edit, and it
  // cannot express a category that is BOTH (Front Casing contributes wall art
  // AND a casing-to-casing dimension).
  // ==========================================================================

  /**
   * Categories whose selection FEEDS a callout. Used for computing the drawing.
   * Front Casing is here because it drives casing-to-casing width.
   */
  const MEASUREMENT_INPUT_CATEGORIES = Object.freeze([
    'Width', 'Height', 'Door Depth', 'Jamb Depth', 'Door Handing',
    'Front Casing', 'Back Casing', 'Front Entablature', 'Back Entablature',
  ]);

  /**
   * Categories where the customer is THINKING ABOUT SIZE. Drives the view
   * auto-switch, and is deliberately a DIFFERENT, smaller set than the one
   * above.
   *
   * 🚨 THESE TWO ARE NOT THE SAME QUESTION, AND CONFLATING THEM FAILED UAT.
   * "Does this category feed a dimension?" and "is the customer sizing the door
   * right now?" have different answers for trim: Front Casing genuinely drives
   * casing-to-casing width, but a customer clicking casing styles is choosing
   * how it LOOKS, not measuring. Deriving the switch from the first question
   * made all four trim categories ambiguous, so they held the current view
   * instead of returning to the product image — which reads as the toggle
   * being stuck.
   *
   * The rule is binary and predictable: these five switch to measurements,
   * everything else switches to the product image. No "hold" state — an
   * auto-switch the customer cannot predict is worse than one that is
   * occasionally not what they'd have picked.
   */
  const MEASUREMENT_MODE_CATEGORIES = Object.freeze([
    'Width', 'Height', 'Door Depth', 'Jamb Depth', 'Door Handing',
  ]);

  /**
   * Splits a category's layer art into product art vs measurement art.
   *
   * The distinction matters and is what makes this survive the overlay cleanup.
   * Width/Height/Door Handing currently carry art — but it is the 90 baked
   * `md-dim-*` dimension PNGs, which are measurements wearing a product layer's
   * clothes. Counting them as product art would classify those categories as
   * "both" and suppress the auto-switch precisely where it is most wanted.
   * Classifying by KIND means the answer is correct today AND stays correct the
   * moment those metafields are cleared, with no code change.
   */
  function categoryArtProfile(classification) {
    const profile = { product: false, measurement: false };
    if (!classification || !classification.products) return profile;

    const products = classification.products.values
      ? Array.from(classification.products.values())
      : Array.from(classification.products);

    const inspect = (url) => {
      if (!url || typeof url !== 'string') return;
      if (url.indexOf('md-dim-') !== -1) profile.measurement = true;
      else profile.product = true;
    };

    for (const product of products) {
      inspect(product.configurator_front_image);
      inspect(product.configurator_back_image);
      const variants = product.variants
        ? (product.variants.values ? Array.from(product.variants.values()) : product.variants)
        : [];
      for (const v of variants) {
        inspect(v.configurator_front_image);
        inspect(v.configurator_back_image);
      }
    }
    return profile;
  }

  /**
   * Should opening this category switch representation?
   *
   * @returns {boolean|null} true = show measurements, false = show product,
   *   null = leave it alone (mirrors detectAutoToggleNeeded's null contract).
   */
  function detectMeasurementModeNeeded(categoryId, classification) {
    // 1. An explicit metaobject override always wins. 'both'/'none' remain the
    //    escape hatch for a category that genuinely should not move the view.
    const role = classification && classification.layerRole;
    if (role === 'measurement') return true;
    if (role === 'product') return false;
    if (role === 'both' || role === 'none') return null;

    // 2. THE LIST IS ABSOLUTE. These five show measurements; every other
    //    category shows the product image. No derivation, no inference.
    //
    //    An earlier version fell through to "does this category carry baked
    //    dimension art?" as a self-correcting safety net. It was removed on
    //    purpose: it is dormant today (those metafields are cleared) but it
    //    meant a sixth category could start showing measurements without anyone
    //    changing this list, which is exactly the unpredictability that failed
    //    UAT the first time. If a new sizing category is ever added, name it
    //    here — the explicitness is the feature.
    return MEASUREMENT_MODE_CATEGORIES.indexOf(categoryId) !== -1;
  }

  /**
   * Retained for the pre-PPD path and tests. Prefer detectMeasurementModeNeeded,
   * which can also answer "leave it alone" — this can only answer yes/no.
   */
  function isMeasurementCategory(categoryId, categoryClassification) {
    const decision = detectMeasurementModeNeeded(categoryId, categoryClassification);
    if (decision !== null) return decision;
    const geo = geometry();
    const list = (geo && geo.MEASUREMENT_CATEGORIES) || [];
    return list.indexOf(categoryId) !== -1;
  }

  window.MurphyDoorConfigurator.dimensionCalculator = {
    computeCallouts,
    readSelectionContext,
    isMeasurementCategory,
    detectMeasurementModeNeeded,
    categoryArtProfile,
    MEASUREMENT_INPUT_CATEGORIES,
    MEASUREMENT_MODE_CATEGORIES,
    parseDimensionTitle,
    parseFractionalInches,
    parseShelfDepth,
    parseHandingTitle,
    trimProfileKey,
    meetsMinusOneRule,
    formatInches,
  };

  isDev() && console.log('[DimensionCalculator] Ready (pure module, no init required)');
})(window);
